import React, { useState } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Button } from '@mui/material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { Users, Activity, TrendingUp, Clock, CheckCircle, AlertTriangle, Target } from 'lucide-react';

interface Patient {
  id: number;
  name: string;
  age: number;
  condition: string;
  admissionDate: string;
  therapyPlan?: {
    type: string;
    frequency: number;
    duration: number;
    goals: string[];
    compliance: number;
  };
  sensorData?: {
    dailySteps: number;
    activeMinutes: number;
    restPeriods: number;
    movementQuality: number;
    walkingSpeed: number;
    strideLength: number;
    balanceScore: number;
    neuroScore: number;
    balanceTrend: 'improving' | 'stable' | 'declining';
    neuroTrend: 'improving' | 'stable' | 'declining';
  };
}

interface GaitMeasurement {
  date: string;
  strideLength: number;
  stepSymmetry: number;
  walkingSpeed: number;
}

interface ComplianceData {
  week: string;
  scheduled: number;
  attended: number;
  completed: number;
}

const generateMockPatients = (): Patient[] => {
  return Array.from({ length: 25 }, (_, i) => {
    // Generate base walking speed between 0.5 and 1.5 m/s (typical range for rehabilitation patients)
    const baseWalkingSpeed = Number((Math.random() * 1.0 + 0.5).toFixed(2));
    
    // Generate stride length between 0.5 and 1.2 meters
    const baseStrideLength = Number((Math.random() * 0.7 + 0.5).toFixed(2));
    
    // Generate balance score (0-100)
    const baseBalanceScore = Math.floor(Math.random() * 30) + 60; // 60-90 range
    
    // Generate neuro score (0-100)
    const baseNeuroScore = Math.floor(Math.random() * 25) + 70; // 70-95 range

    return {
      id: i + 1,
      name: `Patient ${i + 1}`,
      age: Math.floor(Math.random() * 50) + 20,
      condition: ['Stroke', 'Hip Replacement', 'Knee Surgery', 'Spinal Cord Injury'][Math.floor(Math.random() * 4)],
      admissionDate: new Date(Date.now() - Math.random() * 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      therapyPlan: {
        type: ['Physical Therapy', 'Occupational Therapy', 'Gait Training', 'Strength Training'][Math.floor(Math.random() * 4)],
        frequency: Math.floor(Math.random() * 3) + 2,
        duration: Math.floor(Math.random() * 4) + 4,
        goals: [
          'Improve walking stability',
          'Increase range of motion',
          'Enhance balance control',
          'Strengthen core muscles'
        ],
        compliance: Math.floor(Math.random() * 40) + 60
      },
      sensorData: {
        dailySteps: Math.floor(Math.random() * 3000) + 1000,
        activeMinutes: Math.floor(Math.random() * 120) + 30,
        restPeriods: Math.floor(Math.random() * 5) + 2,
        movementQuality: Math.floor(Math.random() * 30) + 70,
        walkingSpeed: baseWalkingSpeed,
        strideLength: baseStrideLength,
        balanceScore: baseBalanceScore,
        neuroScore: baseNeuroScore,
        balanceTrend: ['improving', 'stable', 'declining'][Math.floor(Math.random() * 3)] as 'improving' | 'stable' | 'declining',
        neuroTrend: ['improving', 'stable', 'declining'][Math.floor(Math.random() * 3)] as 'improving' | 'stable' | 'declining'
      }
    };
  });
};

const generateMockGaitData = (): GaitMeasurement[] => {
  const baselineDate = new Date();
  return Array.from({ length: 10 }, (_, i) => {
    const measurementDate = new Date(baselineDate);
    measurementDate.setDate(measurementDate.getDate() - (9 - i) * 3);
    return {
      date: measurementDate.toISOString().split('T')[0],
      strideLength: Math.random() * 0.3 + 0.7,
      stepSymmetry: Math.random() * 20 + 80,
      walkingSpeed: Math.random() * 0.5 + 0.5,
    };
  });
};

const generateComplianceData = (): ComplianceData[] => {
  return Array.from({ length: 6 }, (_, i) => {
    const scheduled = 15;
    const attended = Math.floor(Math.random() * 5) + 10;
    return {
      week: `Week ${i + 1}`,
      scheduled,
      attended,
      completed: Math.floor(Math.random() * (attended - 5)) + 5
    };
  });
};

const RehabilitationAnalytics: React.FC = () => {
  const [patients] = useState<Patient[]>(generateMockPatients());
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [gaitData, setGaitData] = useState<GaitMeasurement[]>([]);
  const [complianceData, setComplianceData] = useState<ComplianceData[]>([]);

  const handlePatientClick = (patient: Patient) => {
    setSelectedPatient(patient);
    setGaitData(generateMockGaitData());
    setComplianceData(generateComplianceData());
  };

  // Calculate summary metrics
  const totalPatients = patients.length;
  const avgAge = Math.round(patients.reduce((sum, p) => sum + p.age, 0) / patients.length);
  const activeTherapies = Math.round(patients.length * 0.7);
  const avgProgress = 75;

  const getComplianceColor = (compliance: number) => {
    if (compliance >= 80) return 'text-green-500';
    if (compliance >= 60) return 'text-yellow-500';
    return 'text-red-500';
  };

  const generateAIRecommendations = (patient: Patient) => {
    const recommendations = [];
    if (patient.sensorData) {
      // Daily activity recommendations
      if (patient.sensorData.dailySteps < 2000) {
        recommendations.push('Increase daily walking activity to improve endurance');
      }
      if (patient.sensorData.movementQuality < 80) {
        recommendations.push('Focus on movement quality during therapy sessions');
      }
      if (patient.sensorData.restPeriods > 4) {
        recommendations.push('Consider adjusting therapy intensity to reduce fatigue');
      }

      // Walking speed recommendations
      if (patient.sensorData.walkingSpeed < 0.8) {
        recommendations.push('Walking speed below target range - consider gait training exercises');
      }

      // Stride length recommendations
      if (patient.sensorData.strideLength < 0.6) {
        recommendations.push('Short stride length detected - focus on range of motion exercises');
      }

      // Balance score recommendations
      if (patient.sensorData.balanceScore < 70) {
        recommendations.push('Balance score indicates need for additional balance training');
      }
      if (patient.sensorData.balanceTrend === 'declining') {
        recommendations.push('Recent decline in balance - schedule balance assessment');
      }

      // Neuro score recommendations
      if (patient.sensorData.neuroScore < 80) {
        recommendations.push('Consider neurological assessment based on gait patterns');
      }
      if (patient.sensorData.neuroTrend === 'declining') {
        recommendations.push('Declining neurological indicators - schedule specialist consultation');
      }
    }

    if (patient.therapyPlan && patient.therapyPlan.compliance < 70) {
      recommendations.push('Schedule follow-up to address therapy attendance');
    }

    return recommendations;
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-6">Rehabilitation Analytics</h2>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <Users className="text-blue-500 mr-2" />
            <h3 className="text-lg font-semibold">Total Patients</h3>
          </div>
          <p className="text-3xl font-bold">{totalPatients}</p>
          <p className="text-gray-500">in rehabilitation</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <Activity className="text-green-500 mr-2" />
            <h3 className="text-lg font-semibold">Active Therapies</h3>
          </div>
          <p className="text-3xl font-bold">{activeTherapies}</p>
          <p className="text-gray-500">ongoing sessions</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <Clock className="text-purple-500 mr-2" />
            <h3 className="text-lg font-semibold">Average Age</h3>
          </div>
          <p className="text-3xl font-bold">{avgAge}</p>
          <p className="text-gray-500">years</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <TrendingUp className="text-orange-500 mr-2" />
            <h3 className="text-lg font-semibold">Avg Progress</h3>
          </div>
          <p className="text-3xl font-bold">{avgProgress}%</p>
          <p className="text-gray-500">recovery rate</p>
        </div>
      </div>
      
      <div className="bg-white rounded-lg shadow mb-6">
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Age</TableCell>
                <TableCell>Condition</TableCell>
                <TableCell>Admission Date</TableCell>
                <TableCell>Compliance</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {patients.map((patient) => (
                <TableRow key={patient.id}>
                  <TableCell>{patient.id}</TableCell>
                  <TableCell>{patient.name}</TableCell>
                  <TableCell>{patient.age}</TableCell>
                  <TableCell>{patient.condition}</TableCell>
                  <TableCell>{patient.admissionDate}</TableCell>
                  <TableCell>
                    <span className={getComplianceColor(patient.therapyPlan?.compliance || 0)}>
                      {patient.therapyPlan?.compliance}%
                    </span>
                  </TableCell>
                  <TableCell>
                    <Button 
                      variant="contained" 
                      color="primary" 
                      onClick={() => handlePatientClick(patient)}
                      className="bg-blue-500 hover:bg-blue-600"
                    >
                      View Progress
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      {selectedPatient && (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-lg shadow border border-blue-200">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-xl font-semibold text-blue-700">
                  Rehabilitation Progress for {selectedPatient.name}
                </h3>
                <p className="text-gray-600">Condition: {selectedPatient.condition}</p>
                <p className="text-gray-600">Admission Date: {selectedPatient.admissionDate}</p>
              </div>
              <div className="text-right">
                <p className="text-lg font-semibold">Therapy Plan</p>
                <p className="text-gray-600">{selectedPatient.therapyPlan?.type}</p>
                <p className="text-gray-600">{selectedPatient.therapyPlan?.frequency}x per week</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Daily Steps</span>
                  <span className="text-lg font-semibold">{selectedPatient.sensorData?.dailySteps}</span>
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Active Minutes</span>
                  <span className="text-lg font-semibold">{selectedPatient.sensorData?.activeMinutes}</span>
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Rest Periods</span>
                  <span className="text-lg font-semibold">{selectedPatient.sensorData?.restPeriods}</span>
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Movement Quality</span>
                  <span className="text-lg font-semibold">{selectedPatient.sensorData?.movementQuality}%</span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h5 className="text-sm font-semibold text-blue-700 mb-2">Walking Speed</h5>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Current</span>
                  <span className="text-lg font-semibold">{selectedPatient.sensorData?.walkingSpeed} m/s</span>
                </div>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg">
                <h5 className="text-sm font-semibold text-blue-700 mb-2">Stride Length</h5>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Average</span>
                  <span className="text-lg font-semibold">{selectedPatient.sensorData?.strideLength} m</span>
                </div>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg">
                <h5 className="text-sm font-semibold text-blue-700 mb-2">Balance Score</h5>
                <div className="flex items-center justify-between">
                  <span className={`text-sm ${selectedPatient.sensorData?.balanceTrend === 'improving' ? 'text-green-600' : selectedPatient.sensorData?.balanceTrend === 'declining' ? 'text-red-600' : 'text-gray-600'}`}>
                    {selectedPatient.sensorData?.balanceTrend}
                  </span>
                  <span className="text-lg font-semibold">{selectedPatient.sensorData?.balanceScore}/100</span>
                </div>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg">
                <h5 className="text-sm font-semibold text-blue-700 mb-2">Neuro Score</h5>
                <div className="flex items-center justify-between">
                  <span className={`text-sm ${selectedPatient.sensorData?.neuroTrend === 'improving' ? 'text-green-600' : selectedPatient.sensorData?.neuroTrend === 'declining' ? 'text-red-600' : 'text-gray-600'}`}>
                    {selectedPatient.sensorData?.neuroTrend}
                  </span>
                  <span className="text-lg font-semibold">{selectedPatient.sensorData?.neuroScore}/100</span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <h4 className="text-lg font-semibold mb-2">Gait Measurements</h4>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={gaitData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="strideLength" stroke="#8884d8" name="Stride Length (m)" />
                    <Line type="monotone" dataKey="stepSymmetry" stroke="#82ca9d" name="Step Symmetry (%)" />
                    <Line type="monotone" dataKey="walkingSpeed" stroke="#ffc658" name="Walking Speed (m/s)" />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div>
                <h4 className="text-lg font-semibold mb-2">Program Compliance</h4>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={complianceData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="week" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="scheduled" fill="#8884d8" name="Scheduled Sessions" />
                    <Bar dataKey="attended" fill="#82ca9d" name="Attended Sessions" />
                    <Bar dataKey="completed" fill="#ffc658" name="Completed Sessions" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <h4 className="text-lg font-semibold mb-4 flex items-center">
              <Target className="text-blue-500 mr-2" />
              AI-Generated Recommendations
            </h4>
            <div className="space-y-4">
              {generateAIRecommendations(selectedPatient).map((recommendation, index) => (
                <div key={index} className="flex items-start p-3 bg-blue-50 rounded-lg">
                  <AlertTriangle className="text-blue-500 mr-2 mt-1" />
                  <p>{recommendation}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <h4 className="text-lg font-semibold mb-4">Therapy Goals</h4>
            <div className="space-y-3">
              {selectedPatient.therapyPlan?.goals.map((goal, index) => (
                <div key={index} className="flex items-center">
                  <CheckCircle className="text-green-500 mr-2" />
                  <span>{goal}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RehabilitationAnalytics; 
